/**
 * Extract error message from error object
 * @param {Error|Object} err - Error object from axios or other sources
 * @param {string} defaultMessage - Default message if no error message found
 * @returns {string} Error message string
 */
export function extractErrorMessage(err, defaultMessage = 'An error occurred. Please try again.') {
  if (typeof err.response?.data?.error === 'string') {
    return err.response.data.error
  }
  if (typeof err.response?.data?.message === 'string') {
    return err.response.data.message
  }
  if (typeof err.message === 'string') {
    return err.message
  }
  return defaultMessage
}

/**
 * Parse data from sessionStorage
 * @param {string} key - SessionStorage key
 * @returns {Object|null} Parsed data or null if not found/invalid
 */
export function parseSessionStorage(key) {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const stored = window.sessionStorage.getItem(key)
    if (!stored) {
      return null
    }
    return JSON.parse(stored)
  } catch (err) {
    console.error(`Failed to parse sessionStorage key "${key}":`, err)
    return null
  }
}

/**
 * Validate enrollment data based on flow type
 * @param {Object} data - Enrollment data object
 * @param {string} type - Flow type: 'claim', 'link', or 'complete'
 * @param {Object} user - Auth0 user object (required for 'claim' type)
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateEnrollmentData(data, type, user = null) {
  if (!data) {
    return { isValid: false, error: 'Enrollment data is missing' }
  }

  switch (type) {
    case 'claim':
      if (!user) {
        return { isValid: false, error: 'User information is missing' }
      }
      if (!user.sub || !user.email) {
        return { isValid: false, error: 'Missing user information' }
      }
      if (!data.account_id || !data.account_address) {
        return { isValid: false, error: 'Missing required enrollment data' }
      }
      return { isValid: true, error: null }

    case 'link':
      if (
        !data.account_id ||
        !data.last_name ||
        !data.street_address_1 ||
        !data.email ||
        !data.first_name
      ) {
        return { isValid: false, error: 'Missing required enrollment data' }
      }
      return { isValid: true, error: null }

    case 'complete':
      const { first_name, last_name, account_address, email } = data
      if (!first_name || !last_name || !account_address || !email) {
        return { isValid: false, error: 'Missing required enrollment information' }
      }
      return { isValid: true, error: null }

    default:
      return { isValid: false, error: 'Invalid flow type' }
  }
}

/**
 * Safely remove item from sessionStorage
 * @param {string} key - SessionStorage key to remove
 */
export function clearSessionStorage(key) {
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(key)
    } catch (err) {
      console.error(`Failed to clear sessionStorage key "${key}":`, err)
    }
  }
}

